import { useState, useEffect } from "react";
import { RefreshCw, Plus, Trash2, Edit, Link as LinkIcon, QrCode, X, ArrowUp, ArrowDown, Copy } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../components/Toast";
import { fmtDateTime } from "../utils/helpers";
import Card from "../components/Card";
import StatusBadge from "../components/StatusBadge";
import Modal from "../components/Modal";
import type { Form, FormField } from "../types";

export default function FormsPage() {
  const { toast } = useToast();
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingForm, setEditingForm] = useState<Form | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareData, setShareData] = useState<{ url: string; qr: string } | null>(null);

  // Form builder state
  const [fName, setFName] = useState("");
  const [fSlug, setFSlug] = useState("");
  const [fFields, setFFields] = useState<FormField[]>([]);

  const fetchForms = async () => {
    setLoading(true);
    try {
      const res = await api.get("/forms");
      if (res.data.ok) setForms(res.data.forms || []);
    } catch (err: any) {
      toast(err.response?.data?.error || "Failed to load forms", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchForms(); }, []);

  const openCreate = () => {
    setEditingForm(null);
    setFName("");
    setFSlug("");
    setFFields([]);
    setIsModalOpen(true);
  };

  const openEdit = async (id: number) => {
    try {
      const res = await api.get(`/forms/${id}`);
      if (res.data.ok) {
        const form = res.data.form;
        setEditingForm(form);
        setFName(form.name);
        setFSlug(form.slug);
        setFFields(form.fields || []);
        setIsModalOpen(true);
      }
    } catch (err: any) {
      toast(err.response?.data?.error || "Failed to load form", "error");
    }
  };

  const saveForm = async () => {
    if (!fName.trim()) { toast("Name is required", "error"); return; }
    if (!fSlug.trim()) { toast("Slug is required", "error"); return; }

    const payload = {
      name: fName.trim(),
      slug: fSlug.trim(),
      fields: fFields,
    };

    try {
      let res;
      if (editingForm) {
        res = await api.put(`/forms/${editingForm.id}`, payload);
      } else {
        res = await api.post("/forms", payload);
      }
      if (res.data.ok) {
        toast(`Form ${editingForm ? "updated" : "created"} successfully`, "success");
        setIsModalOpen(false);
        fetchForms();
      } else {
        toast(res.data.error || "Error", "error");
      }
    } catch (err: any) {
      toast(err.response?.data?.error || "Error", "error");
    }
  };

  const publishForm = async (id: number) => {
    try {
      const res = await api.post(`/forms/${id}/publish`);
      if (res.data.ok) {
        toast("Form published", "success");
        fetchForms();
      } else {
        toast(res.data.error || "Error", "error");
      }
    } catch (err: any) {
      toast(err.response?.data?.error || "Error", "error");
    }
  };

  const archiveForm = async (id: number) => {
    try {
      const res = await api.post(`/forms/${id}/archive`);
      if (res.data.ok) {
        toast("Form archived", "success");
        fetchForms();
      } else {
        toast(res.data.error || "Error", "error");
      }
    } catch (err: any) {
      toast(err.response?.data?.error || "Error", "error");
    }
  };

  
  const deleteForm = async (id: number) => {
    if (!confirm("Eliminare questo form e tutti i lead associati? L'azione è irreversibile.")) return;
    try {
      const res = await api.delete(`/forms/${id}`);
      if (res.data.ok) {
        toast("Form eliminato", "success");
        fetchForms();
      }
    } catch (err: any) {
      toast(err.response?.data?.error || "Errore", "error");
    }
  };
const openShare = async (id: number) => {
    try {
      const res = await api.get(`/forms/${id}/share`);
      if (res.data.ok) {
        setShareData({ url: res.data.public_url || res.data.url, qr: res.data.qr_data_url || res.data.qr });
        setShareModalOpen(true);
      }
    } catch (err: any) {
      toast(err.response?.data?.error || "Error", "error");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast("Copied to clipboard", "success");
  };

  // Field builder helpers
  const addField = () => {
    setFFields([...fFields, { key: "", label: "", type: "text", required: false }]);
  };

  const updateField = (index: number, updates: Partial<FormField>) => {
    const newFields = [...fFields];
    newFields[index] = { ...newFields[index], ...updates };
    setFFields(newFields);
  };

  const removeField = (index: number) => {
    setFFields(fFields.filter((_, i) => i !== index));
  };

  const moveField = (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fFields.length) return;
    const newFields = [...fFields];
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFFields(newFields);
  };

  return (
    <div className="space-y-6" data-testid="forms-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Forms</h1>
          <p className="text-zinc-400 text-sm mt-1">Create and manage public lead capture forms</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-fuchsia-600 hover:bg-fuchsia-500 text-white rounded-xl font-medium transition-colors"
          data-testid="create-form-btn"
        >
          <Plus size={18} /> New Form
        </button>
      </div>

      <Card title="All Forms">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="pb-3 font-medium text-left">ID</th>
                <th className="pb-3 font-medium text-left">Name</th>
                <th className="pb-3 font-medium text-left">Slug</th>
                <th className="pb-3 font-medium text-left">Status</th>
                <th className="pb-3 font-medium text-left">Created</th>
                <th className="pb-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {forms.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-zinc-600 py-8">
                    No forms found
                  </td>
                </tr>
              ) : (
                forms.map((f) => (
                  <tr key={f.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`form-row-${f.id}`}>
                    <td className="py-3 text-zinc-400">{f.id}</td>
                    <td className="py-3 font-medium text-white">{f.name}</td>
                    <td className="py-3 text-zinc-400 font-mono text-xs">{f.slug}</td>
                    <td className="py-3">
                      <StatusBadge status={f.status} />
                    </td>
                    <td className="py-3 text-zinc-400">{fmtDateTime(f.created_at)}</td>
                    <td className="py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Edit - available for ALL statuses */}
                        <button
                          onClick={() => openEdit(f.id)}
                          className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
                          title="Edit"
                          data-testid={`edit-form-${f.id}`}
                        >
                          <Edit size={16} />
                        </button>

                        {f.status === "draft" && (
                          <button
                            onClick={() => publishForm(f.id)}
                            className="px-3 py-1.5 text-xs font-medium bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 rounded-lg transition-colors"
                            data-testid={`publish-form-${f.id}`}
                          >
                            Publish
                          </button>
                        )}

                        {f.status === "published" && (
                          <>
                            <button
                              onClick={() => openShare(f.id)}
                              className="p-1.5 text-zinc-400 hover:text-fuchsia-400 hover:bg-zinc-800 rounded-lg transition-colors"
                              title="Share / QR"
                              data-testid={`share-form-${f.id}`}
                            >
                              <LinkIcon size={16} />
                            </button>
                            <button
                              onClick={() => archiveForm(f.id)}
                              className="px-3 py-1.5 text-xs font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 rounded-lg transition-colors"
                              data-testid={`archive-form-${f.id}`}
                            >
                              Archive
                            </button>
                            <button
                              onClick={() => deleteForm(f.id)}
                              className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-zinc-800 rounded-lg transition-colors"
                              title="Elimina"
                              data-testid={`delete-form-${f.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}

                        {f.status === "archived" && (
                          <>
                            <button
                              onClick={() => publishForm(f.id)}
                              className="px-3 py-1.5 text-xs font-medium bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 rounded-lg transition-colors"
                              data-testid={`republish-form-${f.id}`}
                            >
                              Republish
                            </button>
                            <button
                              onClick={() => deleteForm(f.id)}
                              className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-zinc-800 rounded-lg transition-colors"
                              title="Elimina"
                              data-testid={`delete-form-${f.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingForm ? `Edit Form: ${editingForm.name}` : "Create New Form"}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Form Name</label>
            <input
              value={fName}
              onChange={(e) => setFName(e.target.value)}
              placeholder="Cosmoprof 2026 Leads"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-fuchsia-500"
              data-testid="form-name-input"
            />
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Slug (URL identifier)</label>
            <input
              value={fSlug}
              onChange={(e) => setFSlug(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ""))}
              placeholder="cosmoprof-2026"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white font-mono focus:outline-none focus:border-fuchsia-500"
              data-testid="form-slug-input"
            />
            <p className="text-xs text-zinc-500 mt-1">Public URL: /f/{fSlug || "your-slug"}</p>
          </div>

          {/* Fields Builder */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm text-zinc-400">Fields</label>
              <button
                onClick={addField}
                className="text-xs text-fuchsia-400 hover:text-fuchsia-300 flex items-center gap-1"
                data-testid="add-field-btn"
              >
                <Plus size={14} /> Add Field
              </button>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {fFields.map((field, i) => (
                <div key={i} className="flex items-start gap-2 p-3 bg-zinc-900 rounded-lg" data-testid={`field-row-${i}`}>
                  <div className="flex flex-col gap-1 pt-1">
                    <button
                      onClick={() => moveField(i, "up")}
                      disabled={i === 0}
                      className="text-zinc-500 hover:text-white disabled:opacity-30"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      onClick={() => moveField(i, "down")}
                      disabled={i === fFields.length - 1}
                      className="text-zinc-500 hover:text-white disabled:opacity-30"
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>
                  <div className="flex-1 grid grid-cols-4 gap-2">
                    <div>
                      <input
                        value={field.key}
                        onChange={(e) => updateField(i, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, "") })}
                        placeholder="key"
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-fuchsia-500"
                      />
                    </div>
                    <div>
                      <input
                        value={field.label}
                        onChange={(e) => updateField(i, { label: e.target.value })}
                        placeholder="Label"
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-fuchsia-500"
                      />
                    </div>
                    <div>
                      <select
                        value={field.type}
                        onChange={(e) => updateField(i, { type: e.target.value as any })}
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-fuchsia-500"
                      >
                        <option value="text">Text</option>
                        <option value="textarea">Textarea</option>
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="boolean">Yes/No</option>
                        <option value="rating">Rating (1-5)</option>
                        <option value="select">Select</option>
                        <option value="hidden">Hidden</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateField(i, { required: e.target.checked })}
                          className="rounded border-zinc-600 bg-zinc-900 text-fuchsia-500 focus:ring-fuchsia-500"
                        />
                        <span className="text-sm text-zinc-300">Required</span>
                      </label>
                    </div>
                    
                    {field.type === "select" && (
                      <div className="md:col-span-4">
                        <input
                          value={field.options?.join(", ") || ""}
                          onChange={(e) => updateField(i, { options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                          placeholder="Options (comma separated)"
                          className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-fuchsia-500"
                        />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => removeField(i)}
                    className="text-zinc-500 hover:text-red-400 p-1"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {fFields.length === 0 && (
                <p className="text-sm text-zinc-600 text-center py-4">No fields added yet. Click "Add Field" to start.</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800">
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-zinc-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveForm}
              className="px-4 py-2 bg-fuchsia-600 hover:bg-fuchsia-500 text-white rounded-xl font-medium transition-colors"
              data-testid="save-form-btn"
            >
              {editingForm ? "Update Form" : "Create Form"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Share Modal */}
      <Modal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title="Share Form"
      >
        {shareData && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Public URL</label>
              <div className="flex gap-2">
                <input
                  value={shareData.url}
                  readOnly
                  className="flex-1 bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white font-mono text-sm"
                />
                <button
                  onClick={() => copyToClipboard(shareData.url)}
                  className="p-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition-colors"
                  title="Copy URL"
                >
                  <Copy size={18} />
                </button>
              </div>
            </div>
            {shareData.qr && (
              <div className="text-center">
                <label className="block text-sm text-zinc-400 mb-2">QR Code</label>
                <img src={shareData.qr} alt="QR Code" className="mx-auto rounded-lg" />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
